import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import expenseService from '../../services/ExpenseService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { filterShipmentLocations } from '../../utils/destinationType.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import { IndianRupee, Plus } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import ListPagination from '../common/ListPagination.js';
import { mobileCardClass, listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import { formatRelativeTime, formatIndianDateTime } from '../../utils/localDate.js';
import { X } from 'lucide-react';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';

const REVIEW_ROLES = ['System Admin', 'Admin', 'Accountant'];


const ExpenseList = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAgent = user?.role === 'Agent';
  const canReview = REVIEW_ROLES.includes(user?.role);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [months, setMonths] = useState([]);
  const [locationHubs, setLocationHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(REVIEW_ROLES.includes(user?.role) ? 'pending' : '');
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0, paid: 0 });
  const [decisionExpense, setDecisionExpense] = useState(null);
  const [decisionStatus, setDecisionStatus] = useState('approved');
  const [decisionNote, setDecisionNote] = useState('');
  const [decisionSaving, setDecisionSaving] = useState(false);
  const [statusView, setStatusView] = useState(null);
  const [locationHubFilter, setLocationHubFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  const statuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'booking_reserve', label: 'Booking Reserve' },
    { value: 'delivery_reserve', label: 'Delivery Reserve' },
    { value: 'booking_commission', label: 'Booking Commission' },
    { value: 'delivery_commission', label: 'Delivery Commission' },
  ];

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (categoryFilter) filters.category = categoryFilter;
      if (monthFilter) filters.month = monthFilter;
      if (yearFilter) filters.year = yearFilter;
      if (statusFilter) filters.status = statusFilter;
      if (locationHubFilter) filters.location_hub_id = locationHubFilter;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      const response = await expenseService.getAll(currentPage, perPage, filters);
      setExpenses((prev) => mergeLoadedPage(currentPage, response.data, prev));
      setTotalPages(response.pagination.pages);
      if (response.status_counts) setStatusCounts(response.status_counts);
    } catch (err) {
      Alert.error('Failed to load expenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, monthFilter, yearFilter, statusFilter, locationHubFilter, startDate, endDate, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchExpenses,
    setCurrentPage,
    currentPage,
    resetPageDeps: [categoryFilter, monthFilter, yearFilter, statusFilter, locationHubFilter, startDate, endDate],
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [categoriesRes, monthsRes, hubsRes] = await Promise.all([
        expenseService.getCategories(),
        expenseService.getMonths(),
        locationHubService.getHubsForSelect({ brief: 1 }),
      ]);
      setCategories(categoriesRes.data || []);
      setMonths(monthsRes.data || []);
      setLocationHubs(filterShipmentLocations(hubsRes.data?.hubs || []));
    } catch (err) {
      console.error('Failed to load dropdown data', err);
    }
  };

  const handleDelete = async (expenseId, expenseName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete expense "${expenseName}"?`,
      'Delete Expense'
    );
    
    if (confirmed) {
      try {
        await expenseService.delete(expenseId);
        Alert.success('Expense deleted successfully!');
        
        if (expenses.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchExpenses();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete expense');
      }
    }
  };

  const openDecision = (expense) => {
    setActiveDropdown(null);
    setDecisionExpense(expense);
    setDecisionStatus('approved');
    setDecisionNote('');
  };

  const submitDecision = async () => {
    if (!decisionExpense) return;
    const note = decisionNote.trim();
    if (!note) {
      Alert.error('Add a note for this update');
      return;
    }
    try {
      setDecisionSaving(true);
      await expenseService.decide(decisionExpense.expense_id, decisionStatus, note);
      Alert.success(decisionStatus === 'approved' ? 'Expense approved' : 'Expense rejected');
      setDecisionExpense(null);
      fetchExpenses();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update expense status');
    } finally {
      setDecisionSaving(false);
    }
  };

  const toggleDropdown = (expenseId) => {
    setActiveDropdown(activeDropdown === expenseId ? null : expenseId);
  };

  const isSystemGeneratedExpense = (expense) =>
    ['salary', 'ml_commission', 'ml_commission_deduct'].includes(expense?.source_type);

  const canMutateExpense = (expense) => !isAgent && !isSystemGeneratedExpense(expense);

  const getExpenseExtraItems = (expense) => {
    const items = [];
    if (canReview && !isSystemGeneratedExpense(expense) && expense.status === 'pending') {
      items.push({ label: 'Open request', icon: '↗', onClick: () => openDecision(expense) });
    }
    items.push({ label: 'Expense status', icon: 'ⓘ', onClick: () => { setActiveDropdown(null); setStatusView(expense); } });
    return items;
  };

  const clearFilters = () => {
    setCategoryFilter('');
    setMonthFilter('');
    setYearFilter('');
    setStatusFilter(canReview ? 'pending' : '');
    setLocationHubFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const hasActiveFilters = categoryFilter || monthFilter || yearFilter || statusFilter || locationHubFilter || startDate || endDate;

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700',
      booking_commission: 'bg-indigo-100 text-indigo-700',
      booking_reserve: 'bg-orange-100 text-orange-700',
      delivery_commission: 'bg-violet-100 text-violet-700',
      delivery_reserve: 'bg-amber-100 text-amber-800',
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <DashboardLayout>
              <div className="w-full min-w-0 max-w-full mx-auto">
                <ListPageHeader
                  title="Expenses"
                  icon={IndianRupee}
                  titleClassName="text-secondary"
                  iconClassName="w-5 h-5 text-buttonBg shrink-0"
                  className="mb-4 sm:mb-4"
                >
                  <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/expenses/add')}>
                    Add Expense
                  </Button>
                </ListPageHeader>

                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                  {[
                    { key: 'pending', label: 'Pending', tone: 'border-amber-200 bg-amber-50 text-amber-900' },
                    { key: 'approved', label: 'Approved', tone: 'border-blue-200 bg-blue-50 text-blue-900' },
                    { key: 'rejected', label: 'Rejected', tone: 'border-rose-200 bg-rose-50 text-rose-900' },
                  ].map((chip) => {
                    const active = statusFilter === chip.key;
                    return (
                      <button
                        key={chip.key}
                        type="button"
                        onClick={() => setStatusFilter(active && !canReview ? '' : chip.key)}
                        className={`rounded-xl border px-2 py-2 sm:px-3 sm:py-3 text-left transition ${chip.tone} ${active ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                      >
                        <p className="text-[11px] sm:text-xs font-medium opacity-80">{chip.label}</p>
                        <p className="text-lg sm:text-2xl font-bold tabular-nums leading-tight">{statusCounts[chip.key] || 0}</p>
                      </button>
                    );
                  })}
                </div>

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Category</label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Month</label>
                    <select
                      value={monthFilter}
                      onChange={(e) => setMonthFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Months</option>
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Year</label>
                    <select
                      value={yearFilter}
                      onChange={(e) => setYearFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Years</option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Status</option>
                      {statuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Location Hub</label>
                    <select
                      value={locationHubFilter}
                      onChange={(e) => setLocationHubFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Locations</option>
                      {sortLocationsAsc(locationHubs).map((hub) => (
                        <option key={hub.id} value={hub.id}>
                          {formatLocationDropdownLabel(hub)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>
                </ListFilterPanel>

                {loading && currentPage === 1 ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader fullScreen={false} size="default" message="Loading expenses..." />
                  </div>
                ) : expenses.length === 0 ? (
                  <div className="bg-white p-8 rounded-xl shadow-sm text-center text-gray-600">No expenses found</div>
                ) : (
                  <>
                    {/* Table - Desktop View */}
                    <div className="hidden xl:block bg-white rounded-xl shadow-sm w-full min-w-0">
                      <div className="w-full overflow-x-auto overscroll-x-contain">
                        <table className="w-full min-w-[1100px] border-collapse divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Expense ID</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Category</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Shipment</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px]">Location Hub</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Amount</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[120px]">Vendor</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px]">{t('common.createdBy')}</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                            </tr>
                          </thead>

                          <tbody className="bg-white divide-y divide-gray-200">
                            {expenses.map((expense) => (
                              <tr key={expense.expense_id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <button
                                      onClick={() => navigate(`/expenses/view/${expense.expense_id}`)}
                                      className={listIdLinkClass}
                                    >
                                      {expense.expense_id}
                                    </button>
                                  </td>

                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{expense.category_display}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {expense.shipment_id ? (
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/shipments/view/${expense.shipment_id}`)}
                                      className={listIdLinkClass}
                                    >
                                      {expense.shipment_id}
                                    </button>
                                  ) : '—'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 min-w-[140px]">
                                  <span className="block truncate max-w-[180px]" title={expense.location_hub_name || ''}>
                                    {expense.location_hub_name || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{formatDate(expense.expense_date)}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{formatAmount(expense.amount)}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 min-w-[120px]">
                                  <span className="block truncate max-w-[160px]">{expense.vendor_name || 'N/A'}</span>
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(expense.status)}`}>
                                    {expense.status_display}
                                  </span>
                                  <p className="text-[11px] text-gray-500 mt-1">{formatRelativeTime(expense.created_at)}</p>
                                </td>

                                <td className="px-4 py-3 min-w-[140px]">
                                  <div className="max-w-[180px]">
                                    <p
                                      className="truncate text-sm font-medium text-gray-800"
                                      title={expense.created_by_name || expense.created_by || ''}
                                    >
                                      {expense.created_by_name || expense.created_by || 'N/A'}
                                    </p>
                                    {(expense.location_hub_name || expense.created_by_location_hub) && (
                                      <p
                                        className="truncate text-xs text-gray-500 mt-0.5"
                                        title={expense.location_hub_name || expense.created_by_location_hub}
                                      >
                                        {expense.location_hub_name || expense.created_by_location_hub}
                                      </p>
                                    )}
                                  </div>
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap relative">
                                    <RowActionsMenu
                                      isOpen={activeDropdown === expense.expense_id}
                                      onToggle={() => toggleDropdown(expense.expense_id)}
                                      onClose={() => setActiveDropdown(null)}
                                      menuRef={dropdownRef}
                                      onView={() => navigate(`/expenses/view/${expense.expense_id}`)}
                                      onEdit={canMutateExpense(expense) ? () => navigate(`/expenses/edit/${expense.expense_id}`) : undefined}
                                      onDelete={canMutateExpense(expense) ? () => handleDelete(expense.expense_id, expense.category_display) : undefined}
                                      extraItems={getExpenseExtraItems(expense)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                    </div>

                    {/* Card View - Mobile/Tablet */}
                    <div className="xl:hidden space-y-3 sm:space-y-4">
                      {expenses.map((expense) => (
                        <div key={expense.expense_id} className={`${mobileCardClass} sm:p-5`}>
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <h3
                                className={listIdLinkMobileClass}
                                onClick={() => navigate(`/expenses/view/${expense.expense_id}`)}
                              >
                                {expense.expense_id}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-600">{expense.category_display}</p>
                              {expense.shipment_id && (
                                <p className="text-xs text-gray-500 mt-0.5">Shipment {expense.shipment_id}</p>
                              )}
                              <p className="text-lg sm:text-xl font-bold text-gray-900 mt-1 md:hidden">{formatAmount(expense.amount)}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusBadgeClass(expense.status)}`}>
                                {expense.status_display}
                                {formatRelativeTime(expense.created_at) ? ` · ${formatRelativeTime(expense.created_at)}` : ''}
                              </span>
                              <RowActionsMenu
                                isOpen={activeDropdown === expense.expense_id}
                                onToggle={() => toggleDropdown(expense.expense_id)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/expenses/view/${expense.expense_id}`)}
                                onEdit={canMutateExpense(expense) ? () => navigate(`/expenses/edit/${expense.expense_id}`) : undefined}
                                onDelete={canMutateExpense(expense) ? () => handleDelete(expense.expense_id, expense.category_display) : undefined}
                                extraItems={getExpenseExtraItems(expense)}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                            <div className="hidden md:block">
                              <span className="text-gray-500">Amount:</span>
                              <p className="text-gray-900 font-medium">{formatAmount(expense.amount)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <p className="text-gray-900 font-medium">{formatDate(expense.expense_date)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Location Hub:</span>
                              <p className="text-gray-900 font-medium">{expense.location_hub_name || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Vendor:</span>
                              <p className="text-gray-900 font-medium">{expense.vendor_name || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Month/Year:</span>
                              <p className="text-gray-900 font-medium">{expense.month_year}</p>
                            </div>
                          </div>
                        </div>
                      ))}

                    </div>
                    <ListPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      loading={loading && currentPage > 1}
                      onNext={() => { setLoading(true); setCurrentPage(prev => Math.min(prev + 1, totalPages)); }}
                    />
                  </>
                )}
              </div>

              {decisionExpense && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-sky-950/40 p-3 sm:p-4">
                  <div className="w-full sm:max-w-md bg-white rounded-2xl shadow-xl border border-sky-100 overflow-hidden max-h-[90vh] overflow-y-auto">
                    <div className="bg-sky-50 border-b border-sky-100 px-4 py-3 sm:px-5 sm:py-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="text-base sm:text-lg font-semibold text-sky-950">Open request</h2>
                        <p className="text-xs sm:text-sm text-sky-800 break-all">{decisionExpense.expense_id} · {formatAmount(decisionExpense.amount)}</p>
                      </div>
                      <button type="button" className="p-1 text-sky-700" onClick={() => setDecisionExpense(null)} aria-label="Close">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="p-4 sm:p-5">
                      <p className="text-sm text-gray-700 mb-4 break-words">{decisionExpense.description}</p>
                      <p className="text-xs font-semibold text-sky-900 mb-2">Status</p>
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {['approved', 'rejected'].map((value) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setDecisionStatus(value)}
                            className={`rounded-xl border px-3 py-2.5 text-sm font-semibold capitalize ${decisionStatus === value ? 'border-sky-600 bg-sky-600 text-white' : 'border-sky-200 bg-sky-50 text-sky-900'}`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                      <label className="block text-xs font-semibold text-sky-900 mb-1">Note</label>
                      <textarea
                        value={decisionNote}
                        onChange={(e) => setDecisionNote(e.target.value)}
                        rows={3}
                        className="w-full rounded-xl border border-sky-200 bg-sky-50/40 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                        placeholder={decisionStatus === 'approved' ? 'Why is this approved?' : 'Why is this rejected?'}
                      />
                      <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                        <Button variant="secondary" onClick={() => setDecisionExpense(null)}>Cancel</Button>
                        <Button variant="primary" onClick={submitDecision} disabled={decisionSaving}>
                          {decisionSaving ? 'Saving…' : 'Update status'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {statusView && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-sky-950/40 p-3 sm:p-4" onClick={() => setStatusView(null)}>
                  <div className="w-full sm:max-w-md bg-white rounded-2xl shadow-xl border border-sky-100 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-sky-50 border-b border-sky-100 px-4 py-3 sm:px-5 flex items-start justify-between gap-3">
                      <h2 className="text-base sm:text-lg font-semibold text-sky-950">Expense status</h2>
                      <button type="button" className="p-1 text-sky-700" onClick={() => setStatusView(null)} aria-label="Close"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="p-4 sm:p-5">
                      <StatusAudit expense={statusView} />
                    </div>
                  </div>
                </div>
              )}
    </DashboardLayout>
  );
};

function StatusAudit({ expense }) {
  const decided = expense.status === 'approved' || expense.status === 'rejected';
  const decisionLabel = expense.status === 'rejected' ? 'Rejected' : expense.status === 'approved' ? 'Approved' : 'Decision';
  return (
    <div className="space-y-3 text-sm">
      <div className="rounded-xl bg-sky-50 border border-sky-100 p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">Requested</p>
        <p className="font-semibold text-sky-950 mt-1">{expense.created_by_name || expense.created_by || '—'}</p>
        <p className="text-sky-900">{formatRelativeTime(expense.created_at) || '—'}</p>
        <p className="text-xs text-sky-700/80">{formatIndianDateTime(expense.created_at)}</p>
      </div>
      <div className="rounded-xl bg-sky-50 border border-sky-100 p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">{decisionLabel}</p>
        {decided ? (
          <>
            <p className="font-semibold text-sky-950 mt-1">{expense.approved_by || '—'}</p>
            <p className="text-sky-900">{formatRelativeTime(expense.approved_at) || '—'}</p>
            <p className="text-xs text-sky-700/80">{formatIndianDateTime(expense.approved_at)}</p>
            <p className="mt-2 rounded-lg bg-white border border-sky-100 px-2.5 py-2 text-sky-950 break-words">{expense.status_note || 'No note'}</p>
          </>
        ) : (
          <p className="text-sky-900 mt-1">Still pending. No decision yet.</p>
        )}
      </div>
    </div>
  );
}

export default ExpenseList;